import { useState } from "react";
import { ChevronDown, ChevronRight, Wind, Database, Filter, Calculator, Calendar, Fuel, Zap } from "lucide-react";

const IggFuelConsumption = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const calculations = [
    {
      id: "daily-igg-breakdown",
      title: "IGG Fuel Consumption Breakdown",
      description: "Calculates IGG fuel consumption for each fuel type per day, handling missing data",
      formula: "Daily IGG = {HFO: iggConsHfo, LFO: iggConsLfo, MGO: iggConsMgo, Others: iggConsOthers}",
      subFormulas: [
        "IGG HFO = record?.iggConsHfo || 0",
        "IGG LFO = record?.iggConsLfo || 0",
        "IGG MGO = record?.iggConsMgo || 0",
        "IGG Others = record?.iggConsOthers || 0",
        "Daily IGG Total = HFO + LFO + MGO + Others"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "igg-period-totals",
      title: "IGG Total Calculations",
      description: "Sums IGG consumption for each fuel type across the entire period",
      formula: "IGG_Total_FuelType = Σ(Daily IGG consumption for that fuel type)",
      subFormulas: [
        "Total IGG HFO = Σ(iggConsHfo for each day)",
        "Total IGG LFO = Σ(iggConsLfo for each day)",
        "Total IGG MGO = Σ(iggConsMgo for each day)",
        "Total IGG Others = Σ(iggConsOthers for each day)",
        "Overall IGG Total = Sum of all IGG fuel types"
      ],
      icon: <Database className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
  ];

  const dataStructure = {
    collection: "VesselEmissionRawData",
    iggSpecificFields: [
      { name: "iggConsHfo" },
      { name: "iggConsLfo" },
      { name: "iggConsMgo" },
      { name: "iggConsOthers" }
    ]
  };

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#d5e7f7] rounded-lg">
              <Wind className="w-6 h-6 text-[#566573]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                IGG Fuel Consumption Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Inert Gas Generator fuel usage analysis and tracking methodology
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            <div className="mb-8">
              <div className="mb-8">
                <h4 className="font-semibold text-gray-700 mb-3">IGG Data Source Structure</h4>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="mb-4">
                    <div className="font-medium text-gray-800 mb-2">Collection: {dataStructure.collection}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      {dataStructure.iggSpecificFields.map((field, index) => (
                        <div key={index} className="bg-white p-3 rounded border">
                          <div className="font-medium text-gray-800 text-sm">{field.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Detailed Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {calculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Primary Logic:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Implementation:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IggFuelConsumption;